import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  BarChartOutlined,
  DownloadOutlined,
  EditOutlined,
  FileExcelOutlined,
  SearchOutlined,
  StopOutlined,
  TeamOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, {
  hrmsKeys,
  type AllocationChangeResult,
  type AllocationListParams,
  type AllocationRow,
} from '@/services/hrms.service';
import { useAuthStore } from '@/store/authStore';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { useAllocationProjects } from './shared/useAllocationProjects';
import { ALLOCATION_STATUS_COLOR } from './shared/hrmsStatus';
import AllocationResultSummary from './shared/AllocationResultSummary';

const { Text } = Typography;

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open (current + scheduled)' },
  { value: 'current', label: 'Current' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'Ended', label: 'Ended' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' },
];

const fmt = (d?: string | null) => (d ? dayjs(d).format('DD MMM YYYY') : '—');

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const errorMessage = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

const AllocationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const canManage =
    Boolean(user?.is_super_admin) ||
    Boolean(user?.permissions?.some((p) => p === 'hr.allocation.manage' || p === 'hr.employee.edit'));

  const [employeeId, setEmployeeId] = useState<string | undefined>(searchParams.get('employee') || undefined);
  const [projectId, setProjectId] = useState<string | undefined>(searchParams.get('project') || undefined);
  const [status, setStatus] = useState<string>('open');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AllocationRow | null>(null);
  const [ending, setEnding] = useState<AllocationRow | null>(null);
  const [importResult, setImportResult] = useState<AllocationChangeResult | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editForm] = Form.useForm();
  const [endForm] = Form.useForm();

  const { options: empOptions } = useEmployeeOptions({ employment_status: null });
  const projects = useAllocationProjects();

  const params: AllocationListParams = useMemo(
    () => ({
      employee_id: employeeId,
      project_id: projectId,
      status,
      search: search.trim() || undefined,
    }),
    [employeeId, projectId, status, search]
  );

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.allocations(params),
    queryFn: () => hrmsService.listAllocations(params) as Promise<AllocationRow[]>,
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['hrms', 'allocations'] });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const v = await editForm.validateFields();
      return hrmsService.updateAllocation(editing!.id, {
        allocation_pct: v.allocation_pct,
        effective_to: v.effective_to ? (v.effective_to as Dayjs).format('YYYY-MM-DD') : null,
        role_on_project: v.role_on_project || null,
        notes: v.notes || null,
      });
    },
    onSuccess: () => {
      message.success('Allocation updated');
      setEditing(null);
      refresh();
    },
    onError: (e: any) => {
      if (e?.errorFields) return;
      message.error(errorMessage(e, 'Update failed'));
    },
  });

  const endMutation = useMutation({
    mutationFn: async () => {
      const v = await endForm.validateFields();
      return hrmsService.endAllocation(ending!.id, {
        effective_to: (v.effective_to as Dayjs).format('YYYY-MM-DD'),
        reason: v.reason || undefined,
      });
    },
    onSuccess: () => {
      message.success('Allocation ended');
      setEnding(null);
      refresh();
    },
    onError: (e: any) => {
      if (e?.errorFields) return;
      message.error(errorMessage(e, 'End failed'));
    },
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => hrmsService.importAllocationsExcel(file),
    onSuccess: (r) => {
      setImportResult(r);
      refresh();
    },
    onError: (e: any) => {
      const errors: string[] | undefined = e?.response?.data?.data?.errors;
      if (errors?.length) setImportErrors(errors);
      else message.error(errorMessage(e, 'Import failed'));
    },
  });

  const openEdit = (r: AllocationRow) => {
    editForm.setFieldsValue({
      allocation_pct: r.allocation_pct,
      effective_to: r.effective_to ? dayjs(r.effective_to) : null,
      role_on_project: r.role_on_project || '',
      notes: r.notes || '',
    });
    setEditing(r);
  };

  const openEnd = (r: AllocationRow) => {
    endForm.setFieldsValue({ effective_to: dayjs(), reason: '' });
    setEnding(r);
  };

  const handleTemplate = async () => {
    try {
      saveBlob(await hrmsService.downloadAllocationTemplateBlob(), 'project-allocation-template.xlsx');
    } catch {
      message.error('Template download failed');
    }
  };

  const handleExport = async () => {
    try {
      saveBlob(await hrmsService.exportAllocationsBlob(params), `project-allocations-${dayjs().format('YYYY-MM-DD')}.xlsx`);
    } catch {
      message.error('Export failed');
    }
  };

  const columns: ColumnsType<AllocationRow> = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.employee_name || '—'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.employee_code}
            {r.employment_status && r.employment_status !== 'Active' ? ` · ${r.employment_status}` : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Project',
      key: 'project',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.project_code}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.project_name || ''}
            {r.project_status ? ` · ${r.project_status}` : ''}
          </Text>
        </Space>
      ),
    },
    { title: 'Role', dataIndex: 'role_on_project', width: 150, render: (v) => v || '—' },
    { title: '%', dataIndex: 'allocation_pct', width: 70, align: 'right' },
    { title: 'From', dataIndex: 'effective_from', width: 120, render: fmt },
    { title: 'To', dataIndex: 'effective_to', width: 120, render: (d) => (d ? fmt(d) : 'Open-ended') },
    {
      title: 'Status',
      dataIndex: 'display_status',
      width: 110,
      render: (s: string, r) =>
        r.end_reason ? (
          <Tooltip title={r.end_reason}>
            <Tag color={ALLOCATION_STATUS_COLOR[s]}>{s}</Tag>
          </Tooltip>
        ) : (
          <Tag color={ALLOCATION_STATUS_COLOR[s]}>{s}</Tag>
        ),
    },
    ...(canManage
      ? [
          {
            title: 'Actions',
            key: 'actions',
            fixed: 'right' as const,
            width: 110,
            render: (_: unknown, r: AllocationRow) =>
              r.status === 'Active' ? (
                <Space size={0}>
                  <Tooltip title="Edit">
                    <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                  </Tooltip>
                  <Tooltip title="End allocation">
                    <Button type="text" size="small" danger icon={<StopOutlined />} onClick={() => openEnd(r)} />
                  </Tooltip>
                </Space>
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Project allocations"
          subtitle="Which employees work on which projects, from when, and at what share of their time"
          primaryActions={
            <Space wrap>
              <Button icon={<BarChartOutlined />} onClick={() => navigate('/hr/reports/allocation-exceptions')}>
                Exceptions report
              </Button>
              <Button icon={<FileExcelOutlined />} onClick={handleTemplate}>
                Import template
              </Button>
              {canManage && (
                <Button
                  icon={<UploadOutlined />}
                  loading={importMutation.isPending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Import
                </Button>
              )}
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                Export
              </Button>
              {canManage && (
                <Button type="primary" icon={<TeamOutlined />} onClick={() => navigate('/hr/allocations/bulk')}>
                  Assign / reassign
                </Button>
              )}
            </Space>
          }
        />
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) importMutation.mutate(file);
        }}
      />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="An employee's allocations can total at most 100% on any day"
        description="Payroll uses timesheet project hours to cost labour to projects; when an employee has no project hours in the period, these allocations are used instead. Exited employees' allocations end automatically on their last working day."
      />

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Search employee, project, role"
            allowClear
            prefix={<SearchOutlined />}
            style={{ width: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            allowClear
            placeholder="Employee"
            style={{ width: 280 }}
            options={empOptions}
            value={employeeId}
            onChange={setEmployeeId}
            showSearch
            optionFilterProp="label"
          />
          <Select
            allowClear
            placeholder="Project"
            style={{ width: 300 }}
            options={projects.all}
            value={projectId}
            onChange={setProjectId}
            showSearch
            optionFilterProp="label"
            loading={projects.isLoading}
          />
          <Select style={{ width: 220 }} options={STATUS_OPTIONS} value={status} onChange={setStatus} />
          <Text type="secondary">{rows.length} allocation(s)</Text>
        </Space>
        <Table<AllocationRow>
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1100 }}
          pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'] }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No allocations" /> }}
        />
      </Card>

      <Modal
        title={editing ? `Edit allocation — ${editing.employee_code} on ${editing.project_code}` : ''}
        open={Boolean(editing)}
        onCancel={() => setEditing(null)}
        onOk={() => updateMutation.mutate()}
        okText="Save"
        confirmLoading={updateMutation.isPending}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="allocation_pct" label="Allocation %" rules={[{ required: true }]}>
            <InputNumber min={1} max={100} style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="effective_to" label="Effective to" help="Leave blank if open-ended">
            <DatePicker
              format="DD MMM YYYY"
              disabledDate={(d) => Boolean(editing && d.isBefore(dayjs(editing.effective_from), 'day'))}
            />
          </Form.Item>
          <Form.Item name="role_on_project" label="Role on project">
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={ending ? `End allocation — ${ending.employee_code} on ${ending.project_code}` : ''}
        open={Boolean(ending)}
        onCancel={() => setEnding(null)}
        onOk={() => endMutation.mutate()}
        okText="End allocation"
        okButtonProps={{ danger: true }}
        confirmLoading={endMutation.isPending}
        destroyOnClose
      >
        <Form form={endForm} layout="vertical">
          <Form.Item
            name="effective_to"
            label="Last day on project"
            rules={[{ required: true }]}
            help="If this is before the allocation starts, the allocation is cancelled instead."
          >
            <DatePicker format="DD MMM YYYY" allowClear={false} />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input maxLength={500} placeholder="e.g. Project demobilised" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Allocation import done"
        open={Boolean(importResult)}
        onCancel={() => setImportResult(null)}
        footer={<Button onClick={() => setImportResult(null)}>Close</Button>}
      >
        {importResult && <AllocationResultSummary result={importResult} />}
      </Modal>

      <Modal
        title="Import failed — nothing was imported"
        open={importErrors.length > 0}
        onCancel={() => setImportErrors([])}
        footer={<Button onClick={() => setImportErrors([])}>Close</Button>}
      >
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Fix these rows in the file and upload it again."
        />
        <ul style={{ maxHeight: 320, overflow: 'auto', paddingLeft: 18 }}>
          {importErrors.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      </Modal>
    </WorkspaceLayout>
  );
};

export default AllocationsPage;
